import { useState } from "react";
import { Copy, ExternalLink, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

// Same text the server puts in Outlook drafts — {first_name} can't be personal with BCC.
export const draftText = (body, eventName) => {
  const site = window.location.origin;
  const text = body.replaceAll("{first_name}", "there").replaceAll("{event_name}", eventName);
  return `${text}\n\nView our menu: ${site}/menu\nRequest a quote: ${site}/quote\nLearn more about us: ${site}`;
};

// Plain-text draft preview with "open in my email app" / "save to Outlook Drafts".
export const DraftPreview = ({ event, subject, body, recipients, outlook, onDone, onBack }) => {
  const [busy, setBusy] = useState(false);
  const emails = recipients.map((c) => c.email);
  const subj = subject.replaceAll("{event_name}", event.name).replaceAll("{first_name}", "there");
  const text = draftText(body, event.name);
  const ids = recipients.map((c) => c.id);

  const markSent = async (contactIds = ids) => {
    await api.post(`/admin/events/${event.id}/followup/mark-sent`, { subject, body, contact_ids: contactIds });
    onDone();
  };

  const openApp = async () => {
    const href = `mailto:?bcc=${encodeURIComponent(emails.join(","))}&subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(text)}`;
    if (href.length > 1800 && !window.confirm("This draft is long — some email apps cut it off. If anything is missing, use Copy emails and paste. Open it anyway?")) return;
    window.location.href = href;
    await markSent().catch(() => toast.error("Opened the draft, but couldn't mark contacts as emailed."));
  };

  const saveOutlook = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${event.id}/outlook-draft`, { subject, body, event_name: event.name, contact_ids: ids });
      toast.success(`Draft saved in ${outlook.account_email} → Drafts (${data.count} BCC)`);
      if (data.web_link) window.open(data.web_link, "_blank", "noopener");
      await markSent(data.contact_ids);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't save the Outlook draft.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(emails.join(", ")); toast.success("Addresses copied"); }
    catch { toast.error("Couldn't copy addresses."); }
  };

  const btn = "h-12 rounded-full font-bold text-[12px] uppercase tracking-[0.1em] inline-flex items-center justify-center gap-2 disabled:opacity-60";
  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 pb-44 space-y-3" data-testid="draft-preview">
        <div className="rounded-2xl bg-white border border-black/10 p-4 text-[13.5px] text-brand-charcoal">
          <p><span className="text-brand-charcoal/50">BCC:</span> <span data-testid="draft-bcc-count">{emails.length} {emails.length === 1 ? "person" : "people"}</span></p>
          <p className="mt-1"><span className="text-brand-charcoal/50">Subject:</span> <b data-testid="draft-subject">{subj}</b></p>
          <pre data-testid="draft-body" className="mt-3 whitespace-pre-wrap font-sans leading-relaxed">{text}</pre>
        </div>
        <p className="text-[11.5px] text-brand-charcoal/55">Your email app's signature is added when the draft opens. You can change the From account there too.</p>
      </div>
      <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-black/10 p-3">
        <div className="max-w-lg mx-auto grid gap-2">
          <button type="button" onClick={openApp} data-testid="draft-open-app" className={`${btn} bg-brand-charcoal text-white`}>
            <Mail className="h-4 w-4" /> Open draft in my email app
          </button>
          {outlook?.connected && (
            <button type="button" onClick={saveOutlook} disabled={busy} data-testid="draft-save-outlook" className={`${btn} bg-[#0a64ad] text-white`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Save to Outlook Drafts
            </button>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={onBack} data-testid="draft-back" className={`${btn} border border-black/15`}>Edit</button>
            <button type="button" onClick={copy} data-testid="draft-copy-addresses" className={`${btn} border border-black/15`}>
              <Copy className="h-4 w-4" /> Copy emails
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DraftPreview;
